import { BaseProps } from '../common/props';
import { parseISO } from 'date-fns';
import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { cs } from 'date-fns/locale';
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getWeek,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { mcDropDown, mcNavigateNext, mcNavigatePrev } from '@mc/react-ui-icons';
import { Label } from '../label/Label';
import { Text } from '../typography/Text';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import { findFullWeeksIndexes } from './utils';
import { MonthYear } from './DayWeekPickerGroup';
import { useEffect, useMemo } from 'react';
import { getJavaDate } from '@mc/react-format';
import { useYearListings } from './useYearListings';

export type DayWeekPickerProps = BaseProps & {
  value?: Date[];
  onChange: (date: Date[]) => void;
  locale?: Locale;
  highlightedDays?: Date[];
  /**
   * Pokud je předáváno, je controlled zobrazení měsíce a roku.
   */
  monthYear?: MonthYear;
  /**
   * Pokud je předáváno, je controlled zobrazení měsíce a roku.
   * @param monthYear
   */
  onChangeMonthYear?: (monthYear: MonthYear) => void;
  /**
   * Zobrazit tlačítka pro změnu měsíce.
   */
  showChangeMonth?: boolean;
};

const isHighlighted = (day: Date, highlightedDays?: Date[]) => {
  if (!highlightedDays) {
    return false;
  }
  return highlightedDays.some((highlightedDay) =>
    isSameDay(day, highlightedDay)
  );
};

/**
 * Picker který pracuje s iso string hodnotami datumů.
 */
export type StringDayWeekPickerProps = Omit<
  DayWeekPickerProps,
  'value' | 'onChange'
> & {
  value?: string[];
  onChange: (date: string[]) => void;
};

/**
 * Picker který pracuje s Date hodnotami datumů.
 */
export const StringDayWeekPicker: React.FC<StringDayWeekPickerProps> = ({
  value,
  onChange,
  ...other
}) => {
  const realValue = useMemo(() => {
    if (value && value.length > 0) {
      return value?.map((date) => parseISO(date));
    } else {
      return [];
    }
  }, [value]);

  const handleOnChange = (values: Date[]) => {
    if (values && values.length > 0) {
      // @ts-ignore
      onChange(values.map((date) => getJavaDate(date)));
    } else {
      onChange([]);
    }
  };

  return (
    <DayWeekPicker value={realValue} onChange={handleOnChange} {...other} />
  );
};

export const DayWeekPicker: React.FC<DayWeekPickerProps> = ({
  value,
  className,
  locale = cs,
  onChange,
  monthYear,
  onChangeMonthYear,
  showChangeMonth,
  highlightedDays,
  children,
  ...other
}) => {
  const [showMonthYear, setShowMonthYear] = React.useState<MonthYear>(
    monthYear || {
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
    }
  );

  useEffect(() => {
    if (monthYear) {
      setShowMonthYear(monthYear);
    }
  }, [monthYear]);

  const useDateFormat = 'P';
  const handleNextMonth = () => {
    const startDate = new Date(showMonthYear.year, showMonthYear.month);
    const newDate = addMonths(startDate, 1);
    let newMonthYear = {
      year: newDate.getFullYear(),
      month: newDate.getMonth(),
    };
    setShowMonthYear(newMonthYear);

    onChangeMonthYear?.(newMonthYear);
  };
  const handlePrevMonth = () => {
    const startDate = new Date(showMonthYear.year, showMonthYear.month);
    const newDate = addMonths(startDate, -1);
    let newMonthYear = {
      year: newDate.getFullYear(),
      month: newDate.getMonth(),
    };
    setShowMonthYear(newMonthYear);

    onChangeMonthYear?.(newMonthYear);
  };

  const handleChangeYear = (year: number) => {
    const newMonthYear = {
      year: year,
      month: showMonthYear.month,
    };
    setShowMonthYear(newMonthYear);
    onChangeMonthYear?.(newMonthYear);
  };

  const realValue = value || [];

  const selectedWeeks = findFullWeeksIndexes(realValue);

  // ---
  // Days - seznam dnů pro zobrazení
  const showForDate = new Date(showMonthYear.year, showMonthYear.month);
  const start = startOfWeek(startOfMonth(showForDate), { locale });
  const end = endOfWeek(endOfMonth(showForDate), { locale });
  let day = start;
  const daysDate = [];
  while (+day < +end) {
    daysDate.push(day);
    day = addDays(day, 1);
  }
  // ---

  const formattedMonthAndYear = format(showForDate, 'LLLL yyyy', {
    locale,
  });
  const formattedMonth = format(showForDate, 'LLLL', {
    locale,
  });
  const titledFormattedMonth =
    formattedMonth[0].toLocaleUpperCase() + formattedMonth.slice(1);
  const formattedYear = format(showForDate, 'yyyy', {
    locale,
  });
  const startDate = startOfWeek(new Date(), { weekStartsOn: 1 }); // Monday as the first day of the week
  function daysSinceLastMonday(date: Date) {
    const previousMonday = startOfWeek(date, { weekStartsOn: 1 });
    return differenceInCalendarDays(date, previousMonday);
  }
  function getWeekNumbers(startDate: Date, endDate: Date): number[] {
    // Adjusting start and end date to start and end of their respective weeks
    const adjustedStartDate = startOfWeek(startDate, { weekStartsOn: 1 });
    const adjustedEndDate = endOfWeek(endDate, { weekStartsOn: 1 });

    const weeks = eachWeekOfInterval(
      { start: adjustedStartDate, end: adjustedEndDate },
      { weekStartsOn: 1 }
    );

    return weeks.map((week) => getWeek(week, { weekStartsOn: 1 }));
  }
  function daysUntilNextSunday(date: Date) {
    const nextSunday = endOfWeek(date, { weekStartsOn: 1 });
    return differenceInCalendarDays(nextSunday, date);
  }

  const daysOfWeek = Array.from(Array(7)).map((_, i) => {
    const dayName = format(addDays(startDate, i), 'EEEEEE', { locale });
    return (
      <Label key={i}>
        {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
      </Label>
    );
  });

  const weeksInMonth = getWeekNumbers(
    daysDate[0],
    daysDate[daysDate.length - 1]
  );
  let daysGrid: Date[] = [
    ...Array.from(Array(daysSinceLastMonday(daysDate[0]))),
    ...daysDate,
    ...Array.from(Array(daysUntilNextSunday(daysDate[daysDate.length - 1]))),
  ];
  const today = new Date();

  const handleSelectWeek = (
    week: number,
    weekIndex: number,
    addDay?: boolean
  ) => {
    const chunkSize = 7;
    let chunks = Array.from(
      { length: Math.ceil(daysGrid.length / chunkSize) },
      (_, i) => daysGrid.slice(i * chunkSize, i * chunkSize + chunkSize)
    );
    const selectedChunk: Date[] = chunks[weekIndex].filter(
      (value) => value != undefined
    );
    if (addDay) {
      if (selectedWeeks.some((sweek) => sweek === week)) {
        let newVal = realValue.filter(
          (valueDay) => !selectedChunk.some((day) => isSameDay(valueDay, day))
        );
        onChange(newVal);
        return;
      }

      const newDays = selectedChunk.filter(
        (x) => !realValue.some((y) => isSameDay(x, y))
      );
      let newVal = [...realValue, ...newDays].sort(
        (a, b) => a.getTime() - b.getTime()
      );
      //filter out duplicates
      onChange(newVal);
    } else {
      onChange(selectedChunk);
    }
  };
  const selectDay = (day: Date, addDay?: boolean) => {
    if (addDay) {
      if (realValue.some((valueDay) => isSameDay(valueDay, day))) {
        let newVal = realValue.filter((valueDay) => !isSameDay(valueDay, day));
        onChange(newVal);
        return;
      }
      let newVal = [...realValue, day].sort(
        (a, b) => a.getTime() - b.getTime()
      );
      onChange(newVal);
    } else {
      onChange([day]);
    }
  };

  const yearListings = useYearListings(formattedYear);

  return (
    <div
      className={classNames(className, Classes.DAY_WEEK_PICKER_NEW)}
      {...other}
    >
      <div className={Classes.DAY_WEEK_PICKER_HEADER}>
        {showChangeMonth && (
          <ButtonToolbar className={Classes.DAY_WEEK_PICKER_HEADER_1}>
            <Button
              style={'link'}
              icon={<Icon fixedWidth icon={mcNavigatePrev} />}
              onClick={() => handlePrevMonth()}
              dataTest={'prev-month'}
            />
            <Text
              className={Classes.DAY_WEEK_PICKER_HEADER_MONTH_NAME}
              intent={'primary'}
            >
              {titledFormattedMonth}
            </Text>
            <Button
              style={'link'}
              icon={<Icon fixedWidth icon={mcNavigateNext} />}
              onClick={() => handleNextMonth()}
              dataTest={'next-month'}
            ></Button>
          </ButtonToolbar>
        )}
        <Text
          className={Classes.DAY_WEEK_PICKER_HEADER_YEAR}
          intent={showChangeMonth ? 'primary' : 'default'}
        >
          {showChangeMonth ? formattedYear : formattedMonthAndYear}
        </Text>
        {showChangeMonth ? (
          <Button
            style={'link'}
            icon={<Icon fixedWidth icon={mcDropDown} />}
            menu={() => (
              <Menu style={{ maxHeight: 150, overflowY: 'auto' }}>
                {yearListings.map((year) => (
                  <MenuItem
                    active={year === showMonthYear.year}
                    text={year}
                    onClick={() => handleChangeYear(year)}
                  />
                ))}
              </Menu>
            )}
          ></Button>
        ) : null}
      </div>
      <div className={'d-flex flex-row align-items-end'}>
        <div className={Classes.WEEKS_PICKER}>
          {weeksInMonth.map((week, index) => (
            <div
              onClick={(e) =>
                handleSelectWeek(week, index, e.ctrlKey || e.metaKey)
              }
              className={classNames(Classes.WEEK_PICKER, {
                [Classes.ACTIVE]: selectedWeeks.some((sweek) => sweek === week),
              })}
            >
              {week}
            </div>
          ))}
        </div>
        <div>
          <div className={Classes.DAY_WEEK_PICKER_DAY_NAMES}>{daysOfWeek}</div>
          <div className={Classes.DAY_WEEK_PICKER_DAYS}>
            {daysGrid.map((day) => {
              const active = realValue.some((valueDay) =>
                isSameDay(valueDay, day)
              );
              return day ? (
                <div
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      selectDay(day, true);
                    } else {
                      selectDay(day);
                    }
                  }}
                  className={classNames(Classes.DAY_WEEK_PICKER_DAY, {
                    [Classes.DAY_WEEK_PICKER_DAY_ANOTHER_MONTH]:
                      day.getFullYear() !== showMonthYear.year ||
                      day.getMonth() !== showMonthYear.month,
                    [Classes.DAY_WEEK_PICKER_DAY_TD]: isSameDay(day, today),
                    [Classes.DAY_WEEK_PICKER_DAY_HIGHLIGHTED]: isHighlighted(
                      day,
                      highlightedDays
                    ),
                    [Classes.ACTIVE]: active,
                  })}
                  title={format(day, useDateFormat, { locale })}
                  key={day.getTime()}
                >
                  {day.getDate()}
                </div>
              ) : (
                <div className={Classes.DAY_WEEK_PICKER_DAY}></div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
